import Swiper, { Pagination } from 'swiper';
import 'swiper/swiper-bundle.css';
import gsap from "gsap";
Swiper.use([Pagination]);

const DEFAULT_EFFECT = 'consumer';
const DEFAULT_TIMER_TIME = 3000;
const DEFAULT_TRANSITION_DURATION = 0.5;
const DEFAULT_STAGGER_DELAY = 0.05;
const DEFAULT_SWIPER_OPTIONS = {
    slidesPerView: 1,
    spaceBetween: 0,
    pagination: false,
    watchSlidesProgress: true,
    virtualTranslate: true,
    touchStartPreventDefault: false,
    simulateTouch: false
};

const RESIZE_TIMEOUT = 200;

class HeroSlider {
    constructor(el, effect = DEFAULT_EFFECT, swiperOptions = {}, resizeTimeout = RESIZE_TIMEOUT) {
        this.el = el;
        this.effect = effect;
        this.resizeTimeout = resizeTimeout;

        this.swiperOptions = {...DEFAULT_SWIPER_OPTIONS, ...swiperOptions};
        this.timerTime = DEFAULT_TIMER_TIME;
        this.transitionDuration = DEFAULT_TRANSITION_DURATION;
        this.staggerDelay = DEFAULT_STAGGER_DELAY;
        this.disabled = false;
        this.direction = 1;

        this.sliderItems = [...el.querySelectorAll('.swiper-slide')];

        document.addEventListener("visibilitychange", () => {
            if (document.hidden) {
                this.stopTimer();
            } else {
                this.startTimer();
            }
        });

        this.init();
        this.startTimer();
    }

    initSlides() {
        if(this.sliderItems.length) {
            this.sliderItems.forEach((slide, index) => {
                if(index !== 0) {
                    const slideElements = slide.querySelectorAll('[data-slide-layer]');
                    gsap.set(slideElements, {
                        opacity: 0
                    });
                }
            });
        }
    }

    init() {
        const self = this;

        this.initSlides();
        this.heroSwiper = new Swiper(this.el, {
            ...this.swiperOptions,
            effect: this.effect,
            on: {
                init: (swiper) => {
                    this.setTranslateHero(swiper);
                    this.heroActiveIndex = swiper.activeIndex;

                    const nextSlide = swiper.slides[swiper.activeIndex];
                    const nextSlideElements = nextSlide.querySelectorAll('[data-slide-layer]');

                    gsap.fromTo(nextSlideElements, {
                            x: 50,
                            opacity: 0
                        },
                        {
                            duration: this.transitionDuration,
                            x: 0,
                            opacity: 1,
                            ease: "power2.out",
                            stagger: this.staggerDelay,
                        })
                },
                slideChange: (swiper) => {
                    this.stopTimer();
                    this.setTranslateHero(swiper);
                    let previousIndex = this.heroActiveIndex;

                    const previousSlide = swiper.slides[previousIndex];
                    const nextSlide = swiper.slides[swiper.activeIndex];

                    this.heroActiveIndex = swiper.activeIndex;

                    const nextSlideBackground = nextSlide.querySelector('.align-background');
                    if(previousSlide) {
                        const previousSlideElements = previousSlide.querySelectorAll('[data-slide-layer]:not(.align-background)');
                        const previousSlideBackground = previousSlide.querySelector('.align-background');

                        if(previousSlide) {
                            gsap.set(previousSlide, {
                                zIndex: 3
                            });
                        }

                        if(nextSlideBackground) {
                            gsap.set(nextSlideBackground, {
                                x: 0,
                                opacity: 1
                            });
                        }

                        if(previousSlideElements.length) {
                            gsap.to(previousSlideElements, {
                                duration: this.transitionDuration,
                                x: () => {
                                    return -50 * this.direction
                                },
                                opacity: 0,
                                ease: "power2.out",
                                stagger: {
                                    from: "end",
                                    each: this.staggerDelay
                                },
                                onStart: () => {
                                    this.disabled = true;
                                },
                                onComplete: () => {
                                    this.disabled = false;

                                    gsap.set(previousSlide, {
                                        clearProps: "zIndex"
                                    });
                                }
                            });
                        }
                        if(previousSlideBackground) {
                            gsap.to(previousSlideBackground, {
                                duration: this.transitionDuration,
                                opacity: 0,
                                ease: "power2.out",
                            });
                        }
                    }

                    const nextSlideElements = nextSlide.querySelectorAll('[data-slide-layer]:not(.align-background)');
                    if(nextSlideElements.length) {
                        gsap.fromTo(nextSlideElements, {
                                x: function () {
                                    return 50 * self.direction
                                },
                                opacity: 0
                            },
                            {
                                duration: this.transitionDuration,
                                x: 0,
                                opacity: 1,
                                ease: "power2.out",
                                stagger: this.staggerDelay,
                                delay: previousSlide ? this.transitionDuration : 0,
                                onComplete: () => {
                                    this.startTimer();
                                }
                            })
                    }
                },
            }
        });


        this.el.addEventListener('mousedown', e => {
            this.initialX = e.clientX;
            this.stopTimer();
        });

        this.el.addEventListener('mouseup', e => {
            if (this.disabled) return;

            const deltaX = this.initialX - e.clientX;

            if (deltaX > 0) {
                this.direction = 1;

                if (this.heroSwiper.activeIndex === this.heroSwiper.slides.length - 1) {
                    this.heroSwiper.slideTo(0);
                } else {
                    this.heroSwiper.slideNext();
                }
            } else if (deltaX < 0) {
                this.direction = -1;

                if (this.heroSwiper.activeIndex === 0) {
                    this.heroSwiper.slideTo(this.heroSwiper.slides.length - 1);
                } else {
                    this.heroSwiper.slidePrev();
                }
            }

            this.startTimer();
        });

        document.querySelector('.swiper-prev').addEventListener('click', () => {
            if (this.disabled) return;

            this.direction = -1;

            if(this.heroSwiper.activeIndex === 0){
                this.heroSwiper.slideTo(this.heroSwiper.slides.length - 1);
            }
            else{
                this.heroSwiper.slidePrev();
            }
        });

        document.querySelector('.swiper-next').addEventListener('click', () => {
            if (this.disabled) return;

            this.direction = 1;

            if(this.heroSwiper.activeIndex === this.heroSwiper.slides.length - 1){
                this.heroSwiper.slideTo(0);
            }
            else{
                this.heroSwiper.slideNext();
            }
        });

        window.addEventListener('resize', this.debounce(() => this.onResize(), this.resizeTimeout));
    }

    onResize() {
        this.setTranslateHero(this.heroSwiper);
    }

    debounce(func, timeout = 300) {
        let timer;
        return (...args) => {
            clearTimeout(timer);
            timer = setTimeout(() => { func.apply(this, args); }, timeout);
        };
    }

    startTimer() {
        this.stopTimer();
        this.timer = setTimeout(() => {
            this.direction = 1;
            (this.heroSwiper.activeIndex === this.heroSwiper.slides.length - 1) ? this.heroSwiper.slideTo(0) : this.heroSwiper.slideNext();
        }, this.timerTime);
    }

    stopTimer() {
        clearTimeout(this.timer);
    }

    setTranslateHero(swiper) {
        const slides = [...swiper.slides];
        slides.forEach((slide, index) => {
            let x = -slide.swiperSlideOffset;
            let y = 0;
            gsap.set(slide, {
                x,
                y
            });
        });
    }
}

export default HeroSlider;
